/**
 * LiveIndicator — subtle "Live" badge for figures sourced from the live
 * (chat-service-proxied) current-month usage endpoints (SF-3734/3735 Wave 2).
 *
 * The Eva drill-downs show LIVE current-month numbers on the tiles + per-user
 * rows while the trend charts stay on the (through-yesterday) Mongo rollups.
 * That deliberate mismatch needs to read as intentional, not a bug, so any
 * figure showing live data carries this small badge. It mirrors the live-intent
 * cue the overview tile uses (a tooltip explaining the figure updates without
 * waiting for the nightly cron) and keeps the visual subtle/consistent: a green
 * pulse-dot tag with a hover tooltip. Rendered ONLY when a figure is actually
 * live — callers drop it when they fall back to the rollup so the badge can
 * never claim "live" over a stale number.
 */
import { Tag, Tooltip } from 'antd';

export interface LiveIndicatorProps {
  /**
   * Tooltip copy explaining the live source. Defaults to the same intent the
   * overview live tile uses so the cue reads consistently across Eva views.
   */
  title?: string;
}

const DEFAULT_TITLE =
  'Live current-month usage, updated without waiting for the nightly rollup';

/** Subtle inline "Live" badge for live-sourced figures. */
export const LiveIndicator: React.FC<LiveIndicatorProps> = ({ title = DEFAULT_TITLE }) => (
  <Tooltip title={title}>
    <Tag
      color="green"
      bordered={false}
      style={{ marginInlineStart: 6, fontSize: 11, lineHeight: '16px', cursor: 'default' }}
    >
      ● Live
    </Tag>
  </Tooltip>
);
